/**
 * Haptic feedback helpers built on `navigator.vibrate()`.
 *
 * Vibration only exists on Android/Chromium and some PWAs — desktop browsers,
 * iOS Safari and any device without a motor simply don't implement it. Every
 * helper therefore degrades to a silent no-op and can never throw or break the
 * interaction that triggered it.
 *
 * Patterns are deliberately short. Anything past ~100ms stops reading as a
 * confirmation and starts feeling like an incoming call, so:
 *   light   → a 12ms tick for ordinary taps
 *   medium  → a 20ms tap for a deliberate commit
 *   success → two ticks with a pause, reserved for a completed sale / save
 *   warning → a short double tap for a soft failure
 *   error   → a longer stutter for a hard failure
 *
 * Users who ask the OS to reduce motion get no vibration either: haptics are a
 * decorative confirmation, and the existing POS taps already honour that
 * preference (`prefersReducedMotion`), so these helpers match.
 */

const supported = () =>
  typeof navigator !== "undefined" && typeof navigator.vibrate === "function";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const fire = (pattern) => {
  if (!supported() || prefersReducedMotion()) return false;
  try {
    return navigator.vibrate(pattern);
  } catch {
    return false;
  }
};

/** Ordinary tap — buttons, chips, tabs, list rows. */
export function hapticLight() {
  return fire(12);
}

/** Deliberate commit — add to cart, confirm a picker. */
export function hapticMedium() {
  return fire(20);
}

/** Success — quick sale recorded, appointment saved, attendance marked. */
export function hapticSuccess() {
  return fire([30, 50, 30]);
}

/** Soft failure — optimistic change rolled back, nothing was lost. */
export function hapticWarning() {
  return fire([20, 40, 20]);
}

/** Hard failure — the action could not be completed. */
export function hapticError() {
  return fire([50, 40, 50, 40, 50]);
}

export default {
  hapticLight,
  hapticMedium,
  hapticSuccess,
  hapticWarning,
  hapticError,
};
